import axios from 'axios';
import { toast } from '../components/ui/use-toast';
import { cachedFetch } from '../utils/requestCache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';

class ContactService {
  // Associate contacts with a customer (not a stop)
  async associateContactsWithCustomer(customerId, contactData) {
    try {
      // The customer endpoint expects direct contact data, not associations array
      const response = await axios.post(`${API_BASE_URL}/customers/${customerId}/contacts`, contactData);
      return response.data;
    } catch (error) {
      console.error('Error creating contact for customer:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility - redirects to customer endpoint
  async associateContactsWithStop(stopId, associations) {
    console.warn('associateContactsWithStop is deprecated. Use associateContactsWithCustomer instead.');
    // If we have associations, take the first one and use customer endpoint
    if (associations && associations.length > 0) {
      const firstAssociation = associations[0];
      // Extract contact data from association format
      const contactData = {
        first_name: firstAssociation.first_name || '',
        last_name: firstAssociation.last_name || '',
        email: firstAssociation.email || '',
        phone: firstAssociation.phone || ''
      };
      // Use the customer ID as the stop ID (assuming it's a customer ID)
      return this.associateContactsWithCustomer(stopId, contactData);
    }
    throw new Error('No associations provided');
  }

  // Get contacts for a specific stop
  async getStopContacts(stopId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/stops/${stopId}/contacts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stop contacts:', error);
      // Return empty array if endpoint doesn't exist yet
      if (error.response?.status === 404 || error.response?.status === 422) {
        console.warn('Contact endpoints not implemented yet, returning empty array');
        return [];
      }
      throw error;
    }
  }

  // Get contact details
  async getContactDetails(contactId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/contacts/${contactId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching contact details:', error);
      throw error;
    }
  }

  // Create a new contact
  async createContact(contactData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/contacts`, contactData);
      return response.data;
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  }

  // Update contact information
  async updateContact(contactId, updates) {
    try {
      const response = await axios.put(`${API_BASE_URL}/contacts/${contactId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  }

  // Get contacts by customer ID - NO CACHING, always fresh
  async getCustomerContacts(customerId) {
    try {
      console.log('🌐 contactService.getCustomerContacts - Making FRESH API call for customer:', customerId);
      console.log('🌐 contactService.getCustomerContacts - Full URL will be:', `${API_BASE_URL}/customers/${customerId}/complete-profile`);
      
      // Direct axios call - NO CACHING
      const token = localStorage.getItem('isco_jwt');
      const response = await axios.get(`${API_BASE_URL}/customers/${customerId}/complete-profile`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      console.log('✅ contactService.getCustomerContacts - Fresh response:', response.data);
      
      // Extract contacts from complete profile response
      if (response.data && Array.isArray(response.data.contacts)) {
        console.log('📞 contactService.getCustomerContacts - Returning contacts:', response.data.contacts.length);
        return response.data.contacts;
      } else {
        console.warn('Unexpected response format from customer complete-profile endpoint:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      // Return empty array if endpoint doesn't exist yet
      if (error.response?.status === 404 || error.response?.status === 422) {
        console.warn('Complete-profile endpoint not implemented yet, returning empty array');
        return [];
      }
      throw error;
    }
  }

  // Remove contact from stop
  async removeContactFromStop(stopId, contactId, engagementType = null) {
    try {
      const params = engagementType ? { engagement_type: engagementType } : {};
      const response = await axios.delete(`${API_BASE_URL}/stops/${stopId}/contacts/${contactId}`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error removing contact from stop:', error);
      throw error;
    }
  }

  // Sync contacts from HubSpot (BM only)
  async syncContactsFromHubSpot(limit = 100, after = null) {
    try {
      const params = { limit };
      if (after) params.after = after;
      
      const response = await axios.post(`${API_BASE_URL}/contacts/sync/hubspot`, null, { params });
      return response.data;
    } catch (error) {
      console.error('Error syncing contacts from HubSpot:', error);
      // Return mock success if endpoint doesn't exist yet
      if (error.response?.status === 404 || error.response?.status === 422) {
        console.warn('HubSpot sync endpoints not implemented yet, returning mock success');
        return { synced_count: 0, errors: [], has_more: false, next_after: null };
      }
      throw error;
    }
  }

  // Sync single contact from HubSpot
  async syncSingleContactFromHubSpot(hubspotContactId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/contacts/sync/hubspot/${hubspotContactId}`);
      return response.data;
    } catch (error) {
      console.error('Error syncing single contact from HubSpot:', error);
      throw error;
    }
  }

  // Update contact notes (following the API guide)
  async updateContactNotes(customerId, contactId, notes, engagementStatus = null) {
    try {
      const payload = { notes };
      if (engagementStatus) {
        payload.engagement_status = engagementStatus;
      }
      
      const response = await axios.put(`${API_BASE_URL}/customers/${customerId}/contacts/${contactId}/notes`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating contact notes:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async updateContactAssociation(stopId, contactId, updates) {
    console.warn('updateContactAssociation is deprecated. Use updateContactNotes instead.');
    // For now, we'll assume stopId might be customerId
    return this.updateContactNotes(stopId, contactId, updates.notes, updates.engagement_status);
  }

  // Sync engagement to HubSpot (following the API guide)
  async syncEngagementToHubSpot(stopId, contactIds, notes, customerId) {
    try {
      const requestBody = {
        contact_ids: contactIds,
        notes: notes
      };
      
      // Since customer objects don't have stop_id, use customer endpoint instead
      const actualCustomerId = customerId || stopId;
      
      const response = await axios.post(`${API_BASE_URL}/customers/${actualCustomerId}/sync-engagement`, requestBody);
      return response.data;
    } catch (error) {
      console.error('Error syncing engagement to HubSpot:', error);
      throw error;
    }
  }
}

export default new ContactService();